import type { Exercise } from '../../types.ts';
export const batch6Foundations: Exercise[] = [
  {
    id: 'foundations-acid-base',
    category: 'foundations',
    title: 'Acids, bases and concentration',
    topic: 'Acids, bases and concentration',
    difficulty: 'Challenging',
    reading: {
      title: 'Acids, bases and concentration',
      sections: [
        {
          id: 'protons',
          title: 'Track a transferred proton',
          blocks: [
            {
              kind: 'paragraph',
              text: 'An acid–base reaction can be understood as a transfer rather than a contest between two vaguely defined substances. In the Brønsted–Lowry model, an acid donates a proton, H⁺, and a base accepts one. The labels describe the roles in a particular reaction. A species capable of donating in one reaction may accept in another.',
            },
            {
              kind: 'paragraph',
              text: 'When an acid gives away H⁺, the remaining species is its conjugate base. When a base gains H⁺, the product is its conjugate acid. Members of a conjugate pair differ by exactly one proton. The charge changes by one positive unit because the transferred particle carries a positive charge; the rest of the atomic framework stays the same.',
            },
            {
              kind: 'example',
              title: 'Follow ammonia and water',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'In NH₃ + H₂O ⇌ NH₄⁺ + OH⁻, ammonia accepts a proton and is the base. Water donates it and is the acid. NH₄⁺ is ammonia’s conjugate acid; OH⁻ is water’s conjugate base. The double arrow indicates that the reverse transfer is also possible.',
                },
              ],
            },
            {
              kind: 'table',
              caption: 'Identify pairs, not neighboring symbols',
              headers: ['Original species', 'After proton transfer', 'Relationship'],
              rows: [
                ['NH₃', 'NH₄⁺', 'Base and conjugate acid'],
                ['H₂O', 'OH⁻', 'Acid and conjugate base'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'Water can also accept a proton to become H₃O⁺, called hydronium. In ordinary aqueous explanations, H⁺ is often used as shorthand, but free bare protons are not floating independently through the liquid. Following hydronium makes the water’s role explicit without changing the basic accounting.',
            },
          ],
        },
        {
          id: 'strength',
          title: 'Strength and concentration answer different questions',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Concentration describes how much substance is present per volume. A molar concentration of 0.10 mol/L means one tenth of a mole in each liter of solution. Acid strength describes the extent to which acid molecules transfer protons to water under the relevant conditions. A strong acid ionizes nearly completely in the introductory dilute-water model; a weak acid establishes an equilibrium with substantial un-ionized acid remaining.',
            },
            {
              kind: 'paragraph',
              text: 'A dilute strong acid and a concentrated weak acid are therefore both possible. “Strong” is not another word for “a large amount.” Comparing their pH requires the concentrations and equilibrium behavior, not just the adjectives. Likewise, a weak acid is not necessarily harmless: acid strength is a chemical equilibrium concept rather than a complete safety classification.',
            },
            {
              kind: 'example',
              title: 'Separate two axes',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Imagine one solution containing a small amount of acid whose molecules almost all donate protons, and another containing much more acid whose molecules only partly donate. The first is dilute and strong; the second is concentrated and weak. Without numbers, neither description alone tells us which has more hydronium.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Dilution adds solvent and lowers amount per volume while preserving the total amount of solute if no reaction or loss occurs. Doubling volume halves concentration in that simple balance. For weak acids, the fraction ionized can also change, so halving total acid concentration does not generally halve hydronium concentration exactly.',
            },
          ],
        },
        {
          id: 'ph',
          title: 'A logarithmic measure of hydronium',
          blocks: [
            {
              kind: 'paragraph',
              text: 'For dilute introductory calculations, pH is approximated by minus the base-ten logarithm of hydronium molar concentration. More precisely, the formal definition uses activity; the concentration approximation is the one used here. Logarithms express powers: log₁₀(10⁻³)=−3, so a hydronium concentration of 10⁻³ mol/L corresponds to pH 3.',
            },
            {
              kind: 'equation',
              label: 'Concentration approximation',
              lines: [
                {
                  left: 'pH',
                  right: '−log₁₀[H₃O⁺]',
                  reason: 'Square brackets mean molar concentration in this simplified model.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'A decrease of one pH unit means ten times as much hydronium, not one additional unit of concentration. A decrease of two means a hundredfold increase. The negative sign explains the direction: more hydronium produces a smaller pH. Averaging pH values is not generally the same as mixing the solutions and finding the resulting pH.',
            },
            {
              kind: 'example',
              title: 'Compare two solutions',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'A solution at pH 4 has approximately 10⁻⁴ mol/L hydronium; one at pH 6 has 10⁻⁶. The ratio is 10⁻⁴/10⁻⁶=100. The first has one hundred times the hydronium concentration, even though the numerical pH difference is only two.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'At 25°C, neutral dilute water has equal hydronium and hydroxide concentrations, each about 10⁻⁷ mol/L. Their product is about 10⁻¹⁴, giving pH+pOH≈14. Neutrality means equality of those ion concentrations. The numerical neutral pH depends on temperature, so “neutral always means exactly seven” is too broad.',
            },
          ],
        },
        {
          id: 'balance',
          title: 'Neutralization needs an amount balance',
          blocks: [
            {
              kind: 'paragraph',
              text: 'In the simple strong-acid/strong-base case, hydronium and hydroxide react to form water. To predict whether one remains in excess, compare moles, not just volumes. Moles equal concentration times volume in liters. Equal volumes neutralize exactly only when they supply equal reacting amounts under the reaction’s stoichiometry.',
            },
            {
              kind: 'example',
              title: 'A different mixing calculation',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Mix 20 mL of 0.10 mol/L strong monoprotic acid with 10 mL of 0.10 mol/L strong base. The acid supplies 0.002 mol and the base 0.001 mol of reacting ions. One thousandth of a mole of acid remains after reaction. Assuming additive volumes, it is spread through 0.030 L, giving excess concentration about 0.0333 mol/L.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'The word monoprotic means one relevant proton is donated per acid molecule in this model. A base contributing one hydroxide per formula unit matches that one-to-one reaction. More complex acids, bases or buffers require more detailed bookkeeping; the convenient cancellation is not a universal recipe for all mixtures.',
            },
            {
              kind: 'paragraph',
              text: 'For the questions, identify whether the task concerns transfer roles, amount per volume, equilibrium strength or logarithmic concentration. In the mixing item, calculate each reactant’s moles before considering the remaining volume. In the pH comparison, turn the difference into a power of ten. These are different operations because they answer different physical questions.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'OpenStax Chemistry 2e · Brønsted–Lowry acids and bases',
          url: 'https://openstax.org/books/chemistry-2e/pages/14-1-bronsted-lowry-acids-and-bases',
        },
        {
          title: 'OpenStax Chemistry 2e · pH and pOH',
          url: 'https://openstax.org/books/chemistry-2e/pages/14-2-ph-and-poh',
        },
      ],
      authorship:
        'Original teaching, examples and questions prepared with AI assistance; references support factual checks.',
    },
    questions: [
      {
        id: 'b6-acid-base-1',
        prompt:
          'In NH₃ + H₂O ⇌ NH₄⁺ + OH⁻, which species accepts a proton in the forward reaction?',
        options: [
          {
            id: '0',
            text: 'H₂O',
          },
          {
            id: '1',
            text: 'OH⁻',
          },
          {
            id: '2',
            text: 'NH₃',
          },
          {
            id: '3',
            text: 'NH₄⁺',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'NH₃ gains H⁺ to become NH₄⁺, so it is the forward-reaction base.',
        evidence: 'NH₃ gains H⁺ to become NH₄⁺, so it is the forward-reaction base.',
        sectionIds: ['protons'],
      },
      {
        id: 'b6-acid-base-2',
        prompt: 'Which is the conjugate base of H₂CO₃ after it donates one proton?',
        options: [
          {
            id: '0',
            text: 'HCO₃⁻',
          },
          {
            id: '1',
            text: 'CO₃²⁻',
          },
          {
            id: '2',
            text: 'H₃CO₃⁺',
          },
          {
            id: '3',
            text: 'OH⁻',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Removing one H⁺ gives HCO₃⁻; removing two would be a different step.',
        evidence: 'Removing one H⁺ gives HCO₃⁻; removing two would be a different step.',
        sectionIds: ['protons'],
      },
      {
        id: 'b6-acid-base-3',
        prompt: 'What distinguishes a weak acid from a dilute acid?',
        options: [
          {
            id: '0',
            text: 'Weak means fewer moles per liter; dilute means a smaller ionized fraction.',
          },
          {
            id: '1',
            text: 'Both describe the hydronium concentration without regard to the starting acid.',
          },
          {
            id: '2',
            text: 'A weak acid must have a higher pH than any strong-acid solution, regardless of concentration.',
          },
          {
            id: '3',
            text: 'Weak concerns ionization equilibrium; dilute concerns amount per volume.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'Strength and concentration describe different properties.',
        evidence: 'Strength and concentration describe different properties.',
        sectionIds: ['strength'],
      },
      {
        id: 'b6-acid-base-4',
        prompt: 'Approximately how much larger is [H₃O⁺] at pH 3 than at pH 5?',
        options: [
          {
            id: '0',
            text: '2 times',
          },
          {
            id: '1',
            text: '100 times',
          },
          {
            id: '2',
            text: '10 times',
          },
          {
            id: '3',
            text: '1,000 times',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'A two-unit pH decrease corresponds to 10²=100 times the concentration.',
        evidence: 'A two-unit pH decrease corresponds to 10²=100 times the concentration.',
        sectionIds: ['ph'],
      },
      {
        id: 'b6-acid-base-5',
        prompt: 'In the dilute concentration approximation, [H₃O⁺]=10⁻⁵ mol/L gives what pH?',
        options: [
          {
            id: '0',
            text: '−5',
          },
          {
            id: '1',
            text: '5',
          },
          {
            id: '2',
            text: '9',
          },
          {
            id: '3',
            text: '0.00001',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'The negative logarithm of 10⁻⁵ is 5.',
        evidence: 'The negative logarithm of 10⁻⁵ is 5.',
        sectionIds: ['ph'],
      },
      {
        id: 'b6-acid-base-6',
        prompt: 'What defines a neutral aqueous solution?',
        options: [
          {
            id: '0',
            text: 'Its hydronium concentration is zero.',
          },
          {
            id: '1',
            text: 'Its pH is exactly seven at every temperature.',
          },
          {
            id: '2',
            text: 'It contains equal analytical amounts of any acid and any base, regardless of their strengths.',
          },
          {
            id: '3',
            text: 'Hydronium and hydroxide concentrations are equal.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Neutrality concerns equal acid/base ion concentrations; the numerical pH can vary with temperature.',
        evidence:
          'Neutrality concerns equal acid/base ion concentrations; the numerical pH can vary with temperature.',
        sectionIds: ['ph'],
      },
      {
        id: 'b6-acid-base-7',
        prompt: 'At 25°C, a dilute solution has pH 9. What is its approximate pOH?',
        options: [
          {
            id: '0',
            text: '5',
          },
          {
            id: '1',
            text: '9',
          },
          {
            id: '2',
            text: '14',
          },
          {
            id: '3',
            text: '23',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Under these conditions pH+pOH≈14, so pOH≈5.',
        evidence: 'Under these conditions pH+pOH≈14, so pOH≈5.',
        sectionIds: ['ph'],
      },
      {
        id: 'b6-acid-base-8',
        prompt:
          'A fixed amount of nonreacting solute is diluted from 0.5 L to 2 L. Its concentration becomes what fraction of the original?',
        options: [
          {
            id: '0',
            text: 'Four times',
          },
          {
            id: '1',
            text: 'One half',
          },
          {
            id: '2',
            text: 'One quarter',
          },
          {
            id: '3',
            text: 'Unchanged',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'Volume increases fourfold while solute amount stays fixed.',
        evidence: 'Volume increases fourfold while solute amount stays fixed.',
        sectionIds: ['strength'],
      },
      {
        id: 'b6-acid-base-9',
        prompt:
          'Mix 10 mL of 0.20 mol/L strong monoprotic acid with 20 mL of 0.10 mol/L strong monohydroxide base. Which remains in stoichiometric excess?',
        options: [
          {
            id: '0',
            text: 'Acid',
          },
          {
            id: '1',
            text: 'Neither',
          },
          {
            id: '2',
            text: 'Base',
          },
          {
            id: '3',
            text: 'Cannot be compared without equal volumes',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Both supply 0.002 mol of reacting ions, so neither is in stoichiometric excess.',
        evidence: 'Both supply 0.002 mol of reacting ions, so neither is in stoichiometric excess.',
        sectionIds: ['strength', 'balance'],
      },
      {
        id: 'b6-acid-base-10',
        prompt: 'Why can water be described as amphiprotic?',
        options: [
          {
            id: '0',
            text: 'It can donate or accept a proton depending on its reaction partner.',
          },
          {
            id: '1',
            text: 'It contains both hydrogen and oxygen in the same molecule.',
          },
          {
            id: '2',
            text: 'Its pH is independent of the substances dissolved in it.',
          },
          {
            id: '3',
            text: 'It donates a proton in both directions of every acid–base equilibrium.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Water acts as acid or base depending on the transfer in the particular reaction.',
        evidence:
          'Water acts as acid or base depending on the transfer in the particular reaction.',
        sectionIds: ['protons'],
      },
    ],
  },
  {
    id: 'foundations-sound',
    category: 'foundations',
    title: 'Sound and wave motion',
    topic: 'Sound and wave motion',
    difficulty: 'Challenging',
    reading: {
      title: 'Sound and wave motion',
      sections: [
        {
          id: 'medium',
          title: 'A traveling disturbance is not traveling air',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A vibrating object pushes and pulls nearby air, creating small pressure changes. Neighboring regions respond, so the disturbance travels outward even though individual air molecules mainly oscillate around local positions. Sound transfers energy through the medium; it does not require the same parcel of air to travel all the way from a speaker to a listener.',
            },
            {
              kind: 'paragraph',
              text: 'In ordinary sound waves in air, the particle motion is parallel to the direction of wave travel. This makes the wave longitudinal. Compressions are regions of higher pressure and density, and rarefactions are regions of lower pressure and density. A graph drawn as a wavy line may plot pressure; it is not necessarily a picture of particles moving up and down.',
            },
            {
              kind: 'example',
              title: 'People passing a squeeze',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Imagine a line of people who pass a gentle push to the next person while remaining near their places. The disturbance moves down the line without every person walking to its end. The analogy helps separate transport of a disturbance from bulk transport of matter, though air motion is continuous and not a row of rigid people.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Sound needs a material medium because the disturbance is carried through matter. A vacuum does not carry ordinary sound from one object to another. Light is different: it is an electromagnetic wave and can cross a vacuum. Seeing a distant event before hearing it is also consistent with sound traveling much more slowly than light in air.',
            },
          ],
        },
        {
          id: 'period',
          title: 'Count repetitions in time and space',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Frequency f is the number of cycles passing a point per second, measured in hertz. Period T is the time for one cycle. If five cycles fit in one second, each lasts one fifth of a second, so T=1/f. Wavelength λ is the distance between corresponding points on successive cycles, such as neighboring compressions.',
            },
            {
              kind: 'paragraph',
              text: 'Wave speed connects the time and space descriptions. During one period, one full wavelength moves past a fixed point. Speed is therefore wavelength divided by period, or frequency times wavelength. This is a kinematic relationship, not a claim that increasing frequency always increases speed.',
            },
            {
              kind: 'equation',
              label: 'Travel one wavelength each period',
              lines: [
                {
                  left: 'v',
                  right: 'fλ = λ/T',
                  reason:
                    'Meters per cycle multiplied by cycles per second gives meters per second.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'A different numerical wave',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Suppose a wave travels at 300 m/s and repeats 150 times each second. Its wavelength is 300/150=2 m and its period is 1/150 s. Doubling frequency to 300 Hz in the same approximately nondispersive medium gives wavelength 1 m, while speed stays 300 m/s.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'For ordinary audible sound under fixed air conditions, different frequencies travel at nearly the same speed. The medium’s properties set that speed to a useful approximation. A stationary boundary into a different medium keeps the driven frequency but can change speed and therefore wavelength. Do not hold all three fixed when one changes.',
            },
          ],
        },
        {
          id: 'amplitude',
          title: 'Height of the disturbance is a different variable',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Amplitude measures the size of an oscillation, such as pressure departure from equilibrium. Frequency measures how quickly the oscillation repeats. Increasing amplitude while keeping frequency fixed does not turn the sound into a higher-frequency tone. Perceived pitch is closely related to frequency; perceived loudness depends on intensity and human hearing, among other factors.',
            },
            {
              kind: 'table',
              caption: 'Keep the comparisons separate',
              headers: ['Change under fixed medium conditions', 'Expected wave effect'],
              rows: [
                ['Higher frequency', 'Shorter wavelength'],
                [
                  'Larger pressure amplitude at fixed frequency',
                  'Greater intensity in the simple linear-wave model',
                ],
                ['Longer period', 'Lower frequency'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'For a freely spreading point source in an ideal uniform environment, power crosses larger spherical areas farther away. Sphere area grows with distance squared, so intensity falls as the inverse square of distance when absorption and reflections are neglected. Doubling distance spreads the same power over four times the area, giving one quarter the intensity.',
            },
            {
              kind: 'paragraph',
              text: 'This idealization can fail near a source, in a reflective room or where geometry guides the sound. It is still useful for learning why loudness can diminish without a corresponding change in pitch. Less energy per area does not mean fewer cycles per second.',
            },
          ],
        },
        {
          id: 'echo',
          title: 'An echo includes the return trip',
          blocks: [
            {
              kind: 'paragraph',
              text: 'An echo is reflected sound received after it travels to a surface and back. If a stationary listener emits a brief pulse toward a wall at distance d, the total path is 2d. With speed v and elapsed echo time t, 2d=vt, so d=vt/2. The division by two is about geometry, not a special property of seconds or frequency.',
            },
            {
              kind: 'example',
              title: 'A wall at a different distance',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'At a modeled speed of 340 m/s, an echo returning after 0.50 s has traveled 170 m in total. The wall is 85 m away. Using 170 m as the wall distance would count the outward and return legs as though both went away from the listener.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Changes in observed frequency caused by relative motion are called the Doppler effect. An approaching source packs successive wavefronts closer together in front of it, so a stationary observer there receives them more frequently. After the source passes and recedes, arrivals are farther apart. This explanation concerns received frequency; it does not require the sound’s propagation speed through still air to increase.',
            },
            {
              kind: 'paragraph',
              text: 'For the quiz, identify what is actually measured. A microphone’s pressure-versus-time graph yields a period from successive peaks; a pressure-versus-position graph yields a wavelength. An echo time gives a round-trip distance. Treating every horizontal axis as distance would mix these measurements and produce incorrect units.',
            },
            {
              kind: 'paragraph',
              text: 'When checking arithmetic, multiply the inferred frequency and wavelength to recover the supplied speed, or multiply one-way distance by two before comparing with an echo path. A useful physical answer must satisfy both the numerical relation and the described geometry.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'OpenStax Physics · Sound speed, frequency and wavelength',
          url: 'https://openstax.org/books/physics/pages/14-1-speed-of-sound-frequency-and-wavelength',
        },
      ],
      authorship:
        'Original teaching, examples and questions prepared with AI assistance; references support factual checks.',
    },
    questions: [
      {
        id: 'b6-sound-1',
        prompt: 'In an ordinary sound wave in air, how do particles move relative to wave travel?',
        options: [
          {
            id: '0',
            text: 'They oscillate mainly perpendicular to the direction of wave travel.',
          },
          {
            id: '1',
            text: 'They oscillate mainly parallel to it',
          },
          {
            id: '2',
            text: 'They drift from the source to the listener at the speed of sound.',
          },
          {
            id: '3',
            text: 'They remain stationary while only the spaces between particles move.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'Air sound is longitudinal: particle oscillation is parallel to propagation.',
        evidence: 'Air sound is longitudinal: particle oscillation is parallel to propagation.',
        sectionIds: ['medium'],
      },
      {
        id: 'b6-sound-2',
        prompt: 'A sound has frequency 200 Hz. What is its period?',
        options: [
          {
            id: '0',
            text: '0.02 s',
          },
          {
            id: '1',
            text: '200 s',
          },
          {
            id: '2',
            text: '5 s',
          },
          {
            id: '3',
            text: '0.005 s',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'At 200 cycles each second, one cycle takes 1/200 second, which is 0.005 s. Frequency counts cycles per second; period measures seconds per cycle.',
        evidence:
          'At 200 cycles each second, one cycle takes 1/200 second, which is 0.005 s. Frequency counts cycles per second; period measures seconds per cycle.',
        sectionIds: ['period'],
      },
      {
        id: 'b6-sound-3',
        prompt: 'At speed 340 m/s and frequency 170 Hz, wavelength is what?',
        options: [
          {
            id: '0',
            text: '2 m',
          },
          {
            id: '1',
            text: '0.5 m',
          },
          {
            id: '2',
            text: '170 m',
          },
          {
            id: '3',
            text: '57,800 m',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'A wave travels 340 meters each second while 170 cycles pass, so one cycle occupies 340/170 = 2 meters. That spatial repeat distance is the wavelength.',
        evidence:
          'A wave travels 340 meters each second while 170 cycles pass, so one cycle occupies 340/170 = 2 meters. That spatial repeat distance is the wavelength.',
        sectionIds: ['period'],
      },
      {
        id: 'b6-sound-4',
        prompt:
          'Frequency doubles in the same air while speed stays fixed. What happens to wavelength?',
        options: [
          {
            id: '0',
            text: 'It doubles.',
          },
          {
            id: '1',
            text: 'It remains fixed.',
          },
          {
            id: '2',
            text: 'It halves.',
          },
          {
            id: '3',
            text: 'It quadruples.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'Since v=fλ is fixed, doubling f halves λ.',
        evidence: 'Since v=fλ is fixed, doubling f halves λ.',
        sectionIds: ['period'],
      },
      {
        id: 'b6-sound-5',
        prompt:
          'Larger pressure amplitude at unchanged frequency most directly changes which property?',
        options: [
          {
            id: '0',
            text: 'The wave intensity',
          },
          {
            id: '1',
            text: 'The frequency of the pressure oscillation',
          },
          {
            id: '2',
            text: 'The wavelength in otherwise unchanged air',
          },
          {
            id: '3',
            text: 'The cycle period in otherwise unchanged air',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Amplitude affects intensity; frequency and period remain fixed in the stated comparison.',
        evidence:
          'Amplitude affects intensity; frequency and period remain fixed in the stated comparison.',
        sectionIds: ['amplitude'],
      },
      {
        id: 'b6-sound-6',
        prompt: 'An echo returns after 0.20 s at 340 m/s. How far away is the reflecting wall?',
        options: [
          {
            id: '0',
            text: '68 m',
          },
          {
            id: '1',
            text: '17 m',
          },
          {
            id: '2',
            text: '34 m',
          },
          {
            id: '3',
            text: '340 m',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'The total path is 68 m; the wall is half that distance away.',
        evidence: 'The total path is 68 m; the wall is half that distance away.',
        sectionIds: ['echo'],
      },
      {
        id: 'b6-sound-7',
        prompt: 'Why can ordinary sound not propagate through a vacuum?',
        options: [
          {
            id: '0',
            text: 'Its frequency falls below the audible range when the air pressure is zero.',
          },
          {
            id: '1',
            text: 'All waves, including light, require gas molecules to transfer energy.',
          },
          {
            id: '2',
            text: 'Its speed becomes zero because every wave reflects off the absence of air.',
          },
          {
            id: '3',
            text: 'It requires matter to carry the mechanical disturbance.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'Sound is mechanical; electromagnetic waves need not share this restriction.',
        evidence: 'Sound is mechanical; electromagnetic waves need not share this restriction.',
        sectionIds: ['medium'],
      },
      {
        id: 'b6-sound-8',
        prompt:
          'A pressure-versus-time graph has adjacent peaks 0.01 s apart. What does that spacing directly measure?',
        options: [
          {
            id: '0',
            text: 'Wavelength',
          },
          {
            id: '1',
            text: 'Period',
          },
          {
            id: '2',
            text: 'Propagation distance',
          },
          {
            id: '3',
            text: 'Intensity',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'Time between successive cycles is the period.',
        evidence: 'Time between successive cycles is the period.',
        sectionIds: ['period', 'echo'],
      },
      {
        id: 'b6-sound-9',
        prompt:
          'For an ideal point source with no absorption or reflections, doubling distance changes intensity to what?',
        options: [
          {
            id: '0',
            text: 'One half',
          },
          {
            id: '1',
            text: 'Twice',
          },
          {
            id: '2',
            text: 'One quarter',
          },
          {
            id: '3',
            text: 'Four times',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'The spherical area grows fourfold, so power per area falls to one quarter.',
        evidence: 'The spherical area grows fourfold, so power per area falls to one quarter.',
        sectionIds: ['amplitude'],
      },
      {
        id: 'b6-sound-10',
        prompt:
          'A source approaches a stationary observer through still air. Why can the received pitch rise?',
        options: [
          {
            id: '0',
            text: 'Successive wavefronts arrive more frequently.',
          },
          {
            id: '1',
            text: 'The source increases its emitted frequency simply because it changes position.',
          },
          {
            id: '2',
            text: 'Each wavefront travels faster through the same still air as the source approaches.',
          },
          {
            id: '3',
            text: 'Greater amplitude at a shorter distance necessarily raises the frequency.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Approach compresses received wavefront spacing, increasing observed frequency.',
        evidence: 'Approach compresses received wavefront spacing, increasing observed frequency.',
        sectionIds: ['period', 'echo'],
      },
    ],
  },
  {
    id: 'foundations-indian-ocean',
    category: 'foundations',
    title: 'The Swahili coast and Indian Ocean trade',
    topic: 'The Swahili coast and Indian Ocean trade',
    difficulty: 'Challenging',
    reading: {
      title: 'The Swahili coast and Indian Ocean trade',
      sections: [
        {
          id: 'network',
          title: 'Ports joined sea routes to inland routes',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The Swahili coast of eastern Africa participated in Indian Ocean exchange long before Portuguese fleets arrived near the end of the fifteenth century. Its ports linked inland producers and trading routes with merchants sailing toward Arabia, Persia, India and farther east. A port was not merely a destination at the edge of a continent; it was an interface between different transport networks.',
            },
            {
              kind: 'paragraph',
              text: 'City-states such as Kilwa, Mombasa and Malindi had their own political histories. They should not be collapsed into a single unified coastal empire. Their prosperity depended on local production, relations with inland communities and connections across the sea. Foreign merchants were important participants, but African communities were active producers, traders and political actors rather than an empty shoreline awaiting outside contact.',
            },
            {
              kind: 'example',
              title: 'Follow a hypothetical cargo',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Imagine a valuable material produced inland, carried to a coastal market, loaded onto a ship and resold in another port. Several merchants may handle it along the way. Its appearance at the final destination does not prove that one ship or traveler covered the whole route, or that the destination produced it.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'This network view also explains why control of an intermediate port could be valuable. A ruler could influence access, collect revenue or redirect exchange without controlling every distant producer and consumer. Economic importance can come from a connecting position as well as from direct ownership of a resource.',
            },
          ],
        },
        {
          id: 'monsoon',
          title: 'Seasonal winds shaped a trading calendar',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Monsoon winds change their prevailing direction seasonally across much of the Indian Ocean. Sailors learned to time voyages around these patterns. A favorable outward journey did not mean an equally easy immediate return: merchants might wait for the seasonal reversal. Sailing schedules were therefore linked to climate and accumulated navigational knowledge.',
            },
            {
              kind: 'paragraph',
              text: 'Waiting in ports created time for exchange beyond the unloading of goods. Traders could establish relationships, arrange credit, learn local customs or settle. Some married into coastal communities. The wind pattern did not mechanically create a culture, but it shaped the opportunities and constraints within which people made choices.',
            },
            {
              kind: 'table',
              caption: 'A causal chain rather than a list of facts',
              headers: ['Condition', 'Practical consequence'],
              rows: [
                ['Seasonally favorable winds', 'Voyages are timed to a recurring calendar'],
                ['Waiting for return conditions', 'Longer stays and repeated contacts at ports'],
                ['Reliable local contacts', 'Help with trade and affairs during absences'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'Dhows and other regional vessels were adapted to the routes they served. A sail that permits sailing at an angle to the wind does not allow a vessel to travel directly into the wind without maneuvering. Nor did one vessel type and one wind rule make all ocean routes equally accessible. Geography, currents, weather and ship design remained important.',
            },
            {
              kind: 'example',
              title: 'A scheduling inference',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'If the favorable return winds are months away, a merchant who has already sold the cargo may still have a reason to remain. Interpreting that stay as evidence that trade failed would ignore the seasonal transport constraint. The same observation can have a logistical explanation rather than a commercial one.',
                },
              ],
            },
          ],
        },
        {
          id: 'culture',
          title: 'Exchange changed language and institutions',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Swahili is a Bantu language with substantial vocabulary borrowed from Arabic and other languages. Borrowing does not make its grammatical ancestry disappear. A shared language could help people from different communities communicate, while its vocabulary also records histories of contact. Language evidence should be interpreted as interaction, not as proof that one population simply replaced another.',
            },
            {
              kind: 'paragraph',
              text: 'Islam spread through the coastal world over centuries, associated with merchants, settlement, learning and local political choices. Mosques and schools became part of urban life. Religious change did not erase all earlier practices uniformly, and participation varied across places and periods. A single conversion date for the entire coast would therefore misrepresent a gradual, uneven process.',
            },
            {
              kind: 'paragraph',
              text: 'Imported objects such as ceramics or beads provide evidence of exchange, but their interpretation requires care. A foreign-made bowl found in a coastal house shows that the object reached the site. By itself it does not establish the owner’s birthplace, the route the bowl traveled or the political control of the settlement. Multiple kinds of evidence are needed for those broader claims.',
            },
            {
              kind: 'example',
              title: 'Read an artifact cautiously',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Suppose a locally built house contains an imported ceramic dish. A supported inference is that its occupants had access, directly or indirectly, to goods from outside the region. Claiming the house was built by the dish’s overseas manufacturer would add a link the object alone cannot demonstrate.',
                },
              ],
            },
          ],
        },
        {
          id: 'power',
          title: 'Prosperity and coercion could exist in the same network',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Coastal markets handled varied commodities, including metals, ivory, textiles and other manufactured goods. Enslaved people were also forced into these networks. A description of expanding trade should not treat every movement as voluntary exchange or assume that prosperity was shared equally among merchants, producers, rulers and coerced laborers.',
            },
            {
              kind: 'paragraph',
              text: 'Kilwa’s influence was connected in part to access to gold moving through Sofala from southern African interiors. This illustrates how control of a commercial connection could support political power. It does not mean every gold producer was located within the port itself. Distinguish production, transit and political control when following a commodity.',
            },
            {
              kind: 'paragraph',
              text: 'Portuguese arrival introduced a new armed actor into an existing trading world. Attempts to control ports and routes changed balances of power and met local resistance and negotiation. Saying Europeans “opened” an otherwise isolated ocean would erase centuries of earlier connections. Saying nothing changed would also miss the effects of military intervention.',
            },
            {
              kind: 'paragraph',
              text: 'For the quiz, connect a claim with the kind of evidence that can support it. Seasonal winds explain timing; language and material remains illuminate contact; port control helps explain political influence. None is a complete explanation on its own. The point is to reconstruct relationships among environment, commerce and communities without reducing the history to a sequence of foreign arrivals.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'OpenStax World History · The Swahili coast',
          url: 'https://openstax.org/books/world-history-volume-2/pages/3-3-the-swahili-coast',
        },
        {
          title: 'OpenStax World History · East–West interactions',
          url: 'https://openstax.org/books/world-history-volume-1/pages/12-2-east-west-interactions-in-the-early-middle-ages',
        },
      ],
      authorship:
        'Original teaching, examples and questions prepared with AI assistance; references support factual checks.',
    },
    questions: [
      {
        id: 'b6-indian-ocean-1',
        prompt: 'What role did Swahili ports play in wider exchange?',
        options: [
          {
            id: '0',
            text: 'They mainly bypassed inland exchange to trade directly with European ports in the medieval period.',
          },
          {
            id: '1',
            text: 'They were founded as Portuguese outposts before developing connections inland.',
          },
          {
            id: '2',
            text: 'They functioned chiefly as isolated producers rather than intermediaries in wider routes.',
          },
          {
            id: '3',
            text: 'They connected inland African networks with Indian Ocean routes.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Ports joined inland supply routes to maritime exchange long before European arrival.',
        evidence:
          'Ports joined inland supply routes to maritime exchange long before European arrival.',
        sectionIds: ['network'],
      },
      {
        id: 'b6-indian-ocean-2',
        prompt: 'Why might a merchant remain in a port after selling the cargo?',
        options: [
          {
            id: '0',
            text: 'Return winds followed the same direction year-round, requiring overland return instead.',
          },
          {
            id: '1',
            text: 'The merchant might wait for favorable seasonal return winds.',
          },
          {
            id: '2',
            text: 'Trade rules required merchants to wait until all goods had been bartered with the original seller.',
          },
          {
            id: '3',
            text: 'The sailing season was determined chiefly by a single coast-wide political authority.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'Monsoon timing could separate outward and return sailing seasons.',
        evidence: 'Monsoon timing could separate outward and return sailing seasons.',
        sectionIds: ['monsoon'],
      },
      {
        id: 'b6-indian-ocean-3',
        prompt: 'Which description of Swahili is most accurate?',
        options: [
          {
            id: '0',
            text: 'An Arabic language that adopted some Bantu place names',
          },
          {
            id: '1',
            text: 'A European contact language formed only after Portuguese settlement',
          },
          {
            id: '2',
            text: 'A Bantu language with substantial borrowed vocabulary, including Arabic',
          },
          {
            id: '3',
            text: 'A shared written code without roots in coastal spoken languages',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'Bantu ancestry and borrowing reflect both continuity and contact.',
        evidence: 'Bantu ancestry and borrowing reflect both continuity and contact.',
        sectionIds: ['culture'],
      },
      {
        id: 'b6-indian-ocean-4',
        prompt:
          'An imported ceramic is found in a coastal house. What does it establish most directly?',
        options: [
          {
            id: '0',
            text: 'The object reached the site through exchange or movement.',
          },
          {
            id: '1',
            text: 'The owner was born in the exporting region.',
          },
          {
            id: '2',
            text: 'The exporting state ruled the city.',
          },
          {
            id: '3',
            text: 'One ship carried it directly from its maker to that house.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'The artifact supports connection but not the more specific route, identity or political claims.',
        evidence:
          'The artifact supports connection but not the more specific route, identity or political claims.',
        sectionIds: ['culture'],
      },
      {
        id: 'b6-indian-ocean-5',
        prompt: 'Why could control of an intermediate port create political influence?',
        options: [
          {
            id: '0',
            text: 'The port had to possess the mines or farms that produced every exported good.',
          },
          {
            id: '1',
            text: 'Influence came only from exporting its own manufactured goods, not from transit.',
          },
          {
            id: '2',
            text: 'It could shape access and revenue along connecting routes.',
          },
          {
            id: '3',
            text: 'Imported luxury objects alone prove that the exporting state controlled the port.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Connecting positions can confer commercial leverage without control of the whole network.',
        evidence:
          'Connecting positions can confer commercial leverage without control of the whole network.',
        sectionIds: ['network', 'power'],
      },
      {
        id: 'b6-indian-ocean-6',
        prompt: 'Which description avoids treating the entire coast as one political unit?',
        options: [
          {
            id: '0',
            text: 'A centralized coast-wide state with ports serving as administrative branches of one capital',
          },
          {
            id: '1',
            text: 'A series of city-states with distinct histories and shared trading connections',
          },
          {
            id: '2',
            text: 'A set of foreign enclaves without connections to African hinterlands',
          },
          {
            id: '3',
            text: 'A common language and religion that necessarily implied a single shared sovereign',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'The region contained multiple political communities.',
        evidence: 'The region contained multiple political communities.',
        sectionIds: ['network'],
      },
      {
        id: 'b6-indian-ocean-7',
        prompt: 'What is the most accurate interpretation of Portuguese arrival?',
        options: [
          {
            id: '0',
            text: 'It introduced a new armed actor into an established trading network.',
          },
          {
            id: '1',
            text: 'It began regular overseas trade where earlier ports had served only local markets.',
          },
          {
            id: '2',
            text: 'It replaced existing African and Asian networks entirely as soon as Portuguese ships arrived.',
          },
          {
            id: '3',
            text: 'It added merchants while leaving political power and coercion outside the trading system.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'The network predated arrival, while intervention altered its politics.',
        evidence: 'The network predated arrival, while intervention altered its politics.',
        sectionIds: ['power'],
      },
      {
        id: 'b6-indian-ocean-8',
        prompt: 'How could seasonal waiting contribute to cultural contact?',
        options: [
          {
            id: '0',
            text: 'It confined merchants to ships, reducing repeated contact ashore.',
          },
          {
            id: '1',
            text: 'It made shared institutions unnecessary because visits were always brief.',
          },
          {
            id: '2',
            text: 'It caused every seasonal visitor to settle permanently and abandon return voyages.',
          },
          {
            id: '3',
            text: 'It allowed longer stays and repeated relationships in port communities.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'Long stays could facilitate social and commercial relationships.',
        evidence: 'Long stays could facilitate social and commercial relationships.',
        sectionIds: ['monsoon', 'culture'],
      },
      {
        id: 'b6-indian-ocean-9',
        prompt:
          'Why is describing all movement through these trade networks as voluntary misleading?',
        options: [
          {
            id: '0',
            text: 'Every merchant traveled under the direct command of a single empire.',
          },
          {
            id: '1',
            text: 'Enslaved people were also coerced into movement and labor.',
          },
          {
            id: '2',
            text: 'Exchange involved voluntary merchants but never coerced labor.',
          },
          {
            id: '3',
            text: 'Imported luxury goods establish that everyone participating shared the profits equally.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'The networks included coercion and unequal experiences as well as commerce.',
        evidence: 'The networks included coercion and unequal experiences as well as commerce.',
        sectionIds: ['power'],
      },
      {
        id: 'b6-indian-ocean-10',
        prompt: 'Kilwa’s connection to gold passing through Sofala illustrates which distinction?',
        options: [
          {
            id: '0',
            text: 'A commodity’s exporting port must be the place where it was mined.',
          },
          {
            id: '1',
            text: 'Control of maritime routes implies direct ownership of every inland mine.',
          },
          {
            id: '2',
            text: 'A port can gain from controlling transit without producing the commodity locally.',
          },
          {
            id: '3',
            text: 'A port’s wealth can be inferred only from the size of its own agricultural hinterland.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'Production, transit and control are distinct links in the commodity chain.',
        evidence: 'Production, transit and control are distinct links in the commodity chain.',
        sectionIds: ['network', 'power'],
      },
    ],
  },
  {
    id: 'foundations-plant-transport',
    category: 'foundations',
    title: 'Water and sugar transport in plants',
    topic: 'Water and sugar transport in plants',
    difficulty: 'Challenging',
    reading: {
      title: 'Water and sugar transport in plants',
      sections: [
        {
          id: 'needs',
          title: 'Different materials need different routes',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A plant must connect organs that obtain different resources. Roots absorb water and mineral nutrients from soil. Photosynthetic leaves use light energy to build sugars from carbon dioxide and water. Growing roots, developing fruits and other tissues need supplies even when they are not producing enough sugar themselves. Transport connects these spatially separated activities.',
            },
            {
              kind: 'paragraph',
              text: 'Xylem carries water and dissolved minerals through conducting structures whose mature water-conducting cells are generally dead. Phloem transports sugars and other solutes through living tissue, including sieve-tube elements supported by companion cells in flowering plants. The contrast is functional, not simply “one goes up and one goes down.” Sugar can move toward a sink above or below its source.',
            },
            {
              kind: 'table',
              caption: 'Compare the pathways',
              headers: ['Tissue', 'Main cargo in this chapter', 'Driving picture'],
              rows: [
                [
                  'Xylem',
                  'Water and dissolved minerals',
                  'Water-potential gradient, often maintained by transpiration',
                ],
                [
                  'Phloem',
                  'Sugars and other solutes',
                  'Pressure differences associated with source loading and sink unloading',
                ],
              ],
            },
            {
              kind: 'paragraph',
              text: 'Plants do not obtain most of their dry carbon mass by swallowing soil. Carbon dioxide supplies carbon used to build organic molecules through photosynthesis. Minerals from soil are essential but are not interchangeable with sugars. Calling both “plant food” can conceal the different sources and transport requirements.',
            },
            {
              kind: 'diagram',
              drawing: 'plant-transport',
              caption: 'Water and sugar travel through different tissues.',
              description:
                'Xylem carries water from roots toward leaves, driven mainly by evaporation at the leaves. Phloem carries sugar from a source leaf to sinks such as a growing shoot or storage root; its destination depends on which organs produce and use sugar.',
            },
          ],
        },
        {
          id: 'water',
          title: 'Evaporation helps pull a connected water column',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Transpiration is water loss, mainly as vapor from leaves. Evaporation from moist surfaces lowers leaf water potential and creates tension in the connected xylem water column. Cohesion between water molecules helps transmit the pull through that column; adhesion to surrounding surfaces also contributes to its behavior. Roots supply water that replaces what leaves lose.',
            },
            {
              kind: 'paragraph',
              text: 'Water potential is a measure used to predict the direction water tends to move when a path exists. Water moves from higher to lower water potential. Dissolved solutes lower it, while pressure can raise or lower the total depending on whether water is compressed or under tension. A full calculation can include gravity and other effects; the basic direction rule is enough for this unit.',
            },
            {
              kind: 'example',
              title: 'Compare two connected compartments',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'If compartment A has water potential −0.2 MPa and B has −0.8 MPa, water tends to move from A toward B. A negative number closer to zero is higher. Comparing only the magnitudes, 0.2 and 0.8, would reverse the prediction.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Xylem transport is not powered by a miniature pump in each trunk cell pushing every water molecule upward. The cohesion–tension explanation connects evaporation, water potential and a continuous conducting pathway. Air entering the column can interrupt transport, which is why continuity matters. The account is a mechanism with conditions, not the statement that trees simply “suck” water without explanation.',
            },
          ],
        },
        {
          id: 'stomata',
          title: 'Gas exchange creates a water trade-off',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Stomata are pores regulated by guard cells. Opening them allows carbon dioxide to diffuse into a leaf, supporting photosynthesis, but also provides a route for water vapor to escape. Closing them can conserve water while limiting carbon dioxide entry. The trade-off links transport to carbon acquisition rather than treating evaporation as an unrelated defect.',
            },
            {
              kind: 'paragraph',
              text: 'Guard cells change pore opening through changes in solute content, water movement and turgor. Turgor is pressure of cell contents against the cell wall. In ordinary well-watered conditions it helps support nonwoody tissues; losing it can cause wilting. A wilted leaf does not necessarily prove that its cell walls have permanently broken.',
            },
            {
              kind: 'example',
              title: 'A drying afternoon',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Suppose dry air increases evaporative demand while soil supply is limited. Closing stomata can reduce further loss, but it can also reduce carbon dioxide availability inside the leaf. The response improves one immediate constraint at a cost to another. Predicting only “photosynthesis increases because less water is lost” misses that competing effect.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Waxy cuticles and leaf structures can reduce water loss, but no single adaptation makes all plants behave identically. Many stomata respond to light and water status; some plants use different daily patterns. The quiz uses specified conditions rather than asserting that every species opens every pore only during daylight.',
            },
          ],
        },
        {
          id: 'sugar',
          title: 'Sources and sinks are roles that can change',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A sugar source exports more carbohydrate than it uses locally; a sink imports it for growth, metabolism or storage. A mature photosynthetic leaf is often a source, and a developing fruit or root tip a sink. A storage organ can be a sink while filling and later become a source when reserves support new growth. Location alone does not determine the role.',
            },
            {
              kind: 'paragraph',
              text: 'In the pressure-flow model, loading sugar into phloem near a source lowers water potential, drawing water in and raising pressure. At a sink, sugar is unloaded and used or stored, helping maintain a pressure difference along the pathway. Bulk flow carries sap from higher toward lower pressure. Loading and unloading may use metabolic energy even though the long-distance flow itself follows pressure differences.',
            },
            {
              kind: 'example',
              title: 'A sprouting storage organ',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Before new leaves can export much sugar, stored carbohydrate can be mobilized and sent to a growing shoot. The storage organ is then a source and the shoot a sink. Later, mature leaves may export sugar back into storage, reversing those functional roles without requiring the whole plant to turn upside down.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Phloem transport can run in different directions in different conducting pathways at the same time. That does not mean sap in one tube must flow both directions simultaneously. Distinguish a whole-plant pattern from the direction in a particular source-to-sink connection.',
            },
            {
              kind: 'paragraph',
              text: 'Apply the model by identifying the cargo, the tissue and the driving difference. A question about leaf water loss points toward transpiration and xylem; one about carbon supplied to a fruit points toward sources, sinks and phloem. Then check whether a change improves one resource supply while worsening another, as stomatal closure can do.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'OpenStax Biology 2e · Transport of water and solutes',
          url: 'https://openstax.org/books/biology-2e/pages/30-5-transport-of-water-and-solutes-in-plants',
        },
      ],
      authorship:
        'Original teaching, examples and questions prepared with AI assistance; references support factual checks.',
    },
    questions: [
      {
        id: 'b6-plant-transport-1',
        prompt: 'Which tissue chiefly carries sugars from a mature leaf toward a developing fruit?',
        options: [
          {
            id: '0',
            text: 'Phloem',
          },
          {
            id: '1',
            text: 'Xylem',
          },
          {
            id: '2',
            text: 'Outer bark alone',
          },
          {
            id: '3',
            text: 'Root hairs',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Phloem connects sugar sources with importing sinks.',
        evidence: 'Phloem connects sugar sources with importing sinks.',
        sectionIds: ['needs', 'sugar'],
      },
      {
        id: 'b6-plant-transport-2',
        prompt: 'What helps drive upward water movement in the cohesion–tension model?',
        options: [
          {
            id: '0',
            text: 'Living cells in each mature xylem vessel pump the water upward in successive stages.',
          },
          {
            id: '1',
            text: 'Positive root pressure alone explains water ascent in all tall trees under all conditions.',
          },
          {
            id: '2',
            text: 'Evaporation from leaves creates tension transmitted through connected water.',
          },
          {
            id: '3',
            text: 'Adhesion to the tube wall supplies an upward force independently of leaf water loss.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'Transpiration and cohesion connect leaf water loss to tension in xylem.',
        evidence: 'Transpiration and cohesion connect leaf water loss to tension in xylem.',
        sectionIds: ['water'],
      },
      {
        id: 'b6-plant-transport-3',
        prompt:
          'Water can move between potentials −0.3 and −0.9 MPa. In which direction does it tend to move?',
        options: [
          {
            id: '0',
            text: 'From −0.9 to −0.3 MPa, because that is toward the value closer to zero',
          },
          {
            id: '1',
            text: 'From −0.3 to −0.9',
          },
          {
            id: '2',
            text: 'No net tendency, because both values are negative',
          },
          {
            id: '3',
            text: 'The direction depends only on sugar concentration, not on total water potential',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Water tends to move from higher to lower potential; −0.3 is higher than −0.9.',
        evidence: 'Water tends to move from higher to lower potential; −0.3 is higher than −0.9.',
        sectionIds: ['water'],
      },
      {
        id: 'b6-plant-transport-4',
        prompt: 'What trade-off can stomatal closure create?',
        options: [
          {
            id: '0',
            text: 'Less water loss and greater carbon dioxide entry through the same pores',
          },
          {
            id: '1',
            text: 'Greater water loss but less carbon dioxide entry',
          },
          {
            id: '2',
            text: 'Reduced water loss with no possible effect on photosynthetic carbon supply',
          },
          {
            id: '3',
            text: 'Less water loss but less carbon dioxide entry',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'Closure limits both vapor escape and the route for carbon dioxide entry.',
        evidence: 'Closure limits both vapor escape and the route for carbon dioxide entry.',
        sectionIds: ['stomata'],
      },
      {
        id: 'b6-plant-transport-5',
        prompt: 'A tuber exports stored sugars to a new shoot. Which roles apply at that time?',
        options: [
          {
            id: '0',
            text: 'Tuber sink, shoot source',
          },
          {
            id: '1',
            text: 'Tuber source, shoot sink',
          },
          {
            id: '2',
            text: 'Both source because both are alive',
          },
          {
            id: '3',
            text: 'Both sink because neither is a mature leaf',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'Source and sink refer to net export/import, not fixed organ labels.',
        evidence: 'Source and sink refer to net export/import, not fixed organ labels.',
        sectionIds: ['sugar'],
      },
      {
        id: 'b6-plant-transport-6',
        prompt: 'What can loss of turgor cause in a nonwoody leaf?',
        options: [
          {
            id: '0',
            text: 'Wilting from reduced internal pressure',
          },
          {
            id: '1',
            text: 'More rigid support because the cell wall contracts around the missing water',
          },
          {
            id: '2',
            text: 'Immediate permanent cell death whenever pressure decreases',
          },
          {
            id: '3',
            text: 'Increased xylem pressure that necessarily restores leaf support at once',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Turgor contributes mechanical support; losing it can cause reversible wilting.',
        evidence: 'Turgor contributes mechanical support; losing it can cause reversible wilting.',
        sectionIds: ['stomata'],
      },
      {
        id: 'b6-plant-transport-7',
        prompt: 'Where does much of the carbon in a plant’s dry organic matter originate?',
        options: [
          {
            id: '0',
            text: 'Mineral grains converted directly into carbon',
          },
          {
            id: '1',
            text: 'Water molecules alone',
          },
          {
            id: '2',
            text: 'Carbon dioxide used in photosynthesis',
          },
          {
            id: '3',
            text: 'Sunlight as material carbon atoms',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'Light supplies energy; carbon dioxide supplies carbon atoms.',
        evidence: 'Light supplies energy; carbon dioxide supplies carbon atoms.',
        sectionIds: ['needs'],
      },
      {
        id: 'b6-plant-transport-8',
        prompt: 'How does source loading help drive phloem flow in the pressure-flow model?',
        options: [
          {
            id: '0',
            text: 'Sugar loading draws water out of the source end and lowers its hydrostatic pressure.',
          },
          {
            id: '1',
            text: 'Sugar travels only by diffusion along the full length of the plant.',
          },
          {
            id: '2',
            text: 'Loading sugar reverses transpiration so xylem directly carries the sugar to the sink.',
          },
          {
            id: '3',
            text: 'Sugar loading promotes water entry and higher pressure near the source.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Loading lowers water potential, encouraging water entry and a pressure gradient.',
        evidence:
          'Loading lowers water potential, encouraging water entry and a pressure gradient.',
        sectionIds: ['water', 'sugar'],
      },
      {
        id: 'b6-plant-transport-9',
        prompt: 'Why is “phloem always moves downward” inaccurate?',
        options: [
          {
            id: '0',
            text: 'Sinks can be above or below sources.',
          },
          {
            id: '1',
            text: 'Phloem carries sugar only to roots, but reverses at night.',
          },
          {
            id: '2',
            text: 'Gravity fixes phloem direction while source and sink merely determine speed.',
          },
          {
            id: '3',
            text: 'All leaves are permanently sources and all underground organs permanently sinks.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Direction follows source–sink relations, which can differ among pathways.',
        evidence: 'Direction follows source–sink relations, which can differ among pathways.',
        sectionIds: ['needs', 'sugar'],
      },
      {
        id: 'b6-plant-transport-10',
        prompt:
          'Which statement best distinguishes xylem and phloem conducting cells in flowering plants?',
        options: [
          {
            id: '0',
            text: 'Both transport tissues rely on hollow dead tubes for all long-distance transport.',
          },
          {
            id: '1',
            text: 'Only xylem contains moving water; phloem transports dry sugar particles.',
          },
          {
            id: '2',
            text: 'Mature xylem water-conducting cells are generally dead; phloem transport involves living cells.',
          },
          {
            id: '3',
            text: 'Xylem flow requires each mature conducting cell to maintain an active pumping nucleus.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'The tissues differ in structure and role; phloem sap also contains water.',
        evidence: 'The tissues differ in structure and role; phloem sap also contains water.',
        sectionIds: ['needs'],
      },
    ],
  },
  {
    id: 'foundations-experiments',
    category: 'foundations',
    title: 'Designing a fair experiment',
    topic: 'Designing a fair experiment',
    difficulty: 'Challenging',
    reading: {
      title: 'Designing a fair experiment',
      sections: [
        {
          id: 'comparison',
          title: 'Define the comparison before collecting results',
          blocks: [
            {
              kind: 'paragraph',
              text: 'An experiment deliberately assigns an intervention and measures an outcome. An observational study records existing differences without assigning the exposure of interest. Both can be useful, but they support causal conclusions through different assumptions. Seeing that two groups differ is not enough to establish that the group label caused the difference.',
            },
            {
              kind: 'paragraph',
              text: 'The experimental unit is the smallest entity independently assigned to a treatment. If whole classrooms receive different teaching methods, the classroom is the assignment unit, even if scores are measured for many pupils. Treating every pupil as an independent treatment assignment would overstate how much independent evidence the design contains.',
            },
            {
              kind: 'paragraph',
              text: 'A treatment is a condition imposed by the study, and a response is the measured outcome. Specify both in operational terms. “Better growth” is vague; plant height after a fixed number of days is measurable, though it captures only one aspect of growth. Deciding the outcome after seeing which measurement looks favorable risks selecting noise.',
            },
            {
              kind: 'example',
              title: 'A fertilizer comparison',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Assign individual pots to fertilizer A or B, give them the same planned watering schedule and measure dry plant mass after six weeks. The pot is the experimental unit, fertilizer is the treatment and dry mass is the response. Measuring each plant twice improves measurement checking but does not create a second independently treated pot.',
                },
              ],
            },
          ],
        },
        {
          id: 'assignment',
          title: 'Random allocation and random sampling do different jobs',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Random assignment uses chance to allocate experimental units among treatments. It makes treatment allocation independent of preexisting characteristics in the design, reducing systematic confounding. Confounding occurs when another variable differs along with the treatment and can also affect the outcome, making their effects difficult to separate.',
            },
            {
              kind: 'paragraph',
              text: 'Randomization does not guarantee identical groups in a particular finite experiment. Chance imbalances remain possible, especially with few units. Statistical uncertainty and design checks are still needed. The benefit is a defensible allocation mechanism and balance on average over possible assignments, not a magical elimination of every other influence.',
            },
            {
              kind: 'paragraph',
              text: 'Random sampling concerns how units enter the study from a population. It supports generalization to that population when implemented appropriately. Random assignment concerns comparisons within the recruited sample. Volunteers can be randomly assigned, giving a stronger within-study comparison without making the volunteers a representative sample of everyone.',
            },
            {
              kind: 'table',
              caption: 'Ask which randomness is present',
              headers: ['Design step', 'Main purpose'],
              rows: [
                [
                  'Select people randomly from a population',
                  'Support population representativeness',
                ],
                [
                  'Assign recruited people randomly to conditions',
                  'Reduce systematic confounding of treatment',
                ],
                [
                  'Measure the same person repeatedly',
                  'Improve information about that person, not independent recruitment',
                ],
              ],
            },
            {
              kind: 'example',
              title: 'Two methods, one confounder',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'If every morning class gets method A and every evening class gets method B, time of day is tied to method. A score difference could reflect either. Randomly assigning methods within each time period separates the planned comparison from that particular scheduling difference.',
                },
              ],
            },
          ],
        },
        {
          id: 'control',
          title: 'Keep comparison conditions informative',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A control condition supplies a reference: no intervention, usual practice or another meaningful alternative. What counts as a useful control depends on the question. Comparing a new lesson with no activity answers something different from comparing it with an equally long established lesson. Equal attention can matter when attention itself affects engagement.',
            },
            {
              kind: 'paragraph',
              text: 'Blinding means keeping specified people unaware of treatment allocation when feasible. Participant blinding can reduce expectation-driven differences; assessor blinding can reduce measurement or scoring bias. The label “double blind” can be used inconsistently, so explicitly stating who is unaware is clearer than relying on the label alone.',
            },
            {
              kind: 'paragraph',
              text: 'Randomization and blinding solve different problems. An assessor can know a perfectly random assignment and still score differently because of expectations. Conversely, blinded measurement does not repair a treatment comparison in which all stronger students were deliberately assigned to one method.',
            },
            {
              kind: 'example',
              title: 'Blind a scorer',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Students write responses after receiving one of two randomized lessons. Remove lesson labels before independent graders score the responses using a common rubric. This does not prevent students from recognizing their lesson, but it can prevent graders’ knowledge of treatment from influencing scores. The design should claim assessor blinding specifically.',
                },
              ],
            },
          ],
        },
        {
          id: 'precision',
          title: 'Use structure without inventing independence',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Blocking groups units by a relevant pre-treatment characteristic, then randomizes treatments within those groups. For plants, a block might be shelf position if light differs across the room. Comparing treatments within blocks can reduce variation attributable to the blocking variable. The blocks must not be confused with treatment groups; each block should contain the relevant treatment comparison.',
            },
            {
              kind: 'paragraph',
              text: 'A paired design compares related observations, such as two measurements on the same participant under different conditions. Pairing can remove stable person-to-person differences, but order and carryover effects may matter. Randomizing or counterbalancing order helps address practice or fatigue; a treatment whose effects persist may require a different design.',
            },
            {
              kind: 'paragraph',
              text: 'Replication means having independent experimental units receiving the conditions. Ten readings from one treated tank do not equal ten independently treated tanks. Repeated measurements reveal within-unit behavior, while multiple units reveal variation across independently assigned entities. An analysis must respect both levels.',
            },
            {
              kind: 'paragraph',
              text: 'Attrition also affects interpretation. If one treatment loses many participants and only completers are compared, the remaining groups may no longer reflect the original randomized comparison. Reasons for missing outcomes and the planned analysis matter. Random assignment at the start is valuable but does not make later missing data irrelevant.',
            },
            {
              kind: 'paragraph',
              text: 'For the quiz, identify the unit, the allocation rule, the comparison condition and the measurement process before interpreting an outcome. Ask whether a stated problem concerns representativeness, confounding, observer bias, order effects or missingness. Naming a study “an experiment” does not answer these questions automatically; the details determine what conclusions are defensible.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'OpenStax Introductory Statistics 2e · Experimental design',
          url: 'https://openstax.org/books/introductory-statistics-2e/pages/1-4-experimental-design-and-ethics',
        },
      ],
      authorship:
        'Original teaching, examples and questions prepared with AI assistance; references support factual checks.',
    },
    questions: [
      {
        id: 'b6-experiments-1',
        prompt:
          'Whole classrooms are randomly assigned to teaching methods, and each pupil is tested. What is the assignment unit?',
        options: [
          {
            id: '0',
            text: 'Each test question',
          },
          {
            id: '1',
            text: 'Each pupil independently',
          },
          {
            id: '2',
            text: 'Each classroom',
          },
          {
            id: '3',
            text: 'Each repeated score',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Treatment is assigned at classroom level, so classroom is the experimental unit.',
        evidence:
          'Treatment is assigned at classroom level, so classroom is the experimental unit.',
        sectionIds: ['comparison'],
      },
      {
        id: 'b6-experiments-2',
        prompt: 'What is the main role of random assignment?',
        options: [
          {
            id: '0',
            text: 'Guarantee identical groups in every realized experiment',
          },
          {
            id: '1',
            text: 'Reduce systematic association between treatment and preexisting characteristics',
          },
          {
            id: '2',
            text: 'Make volunteers representative of the entire population',
          },
          {
            id: '3',
            text: 'Eliminate all measurement error',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Random assignment reduces systematic confounding but chance imbalance remains.',
        evidence: 'Random assignment reduces systematic confounding but chance imbalance remains.',
        sectionIds: ['assignment'],
      },
      {
        id: 'b6-experiments-3',
        prompt:
          'Volunteers are randomly assigned to two treatments. Which conclusion is warranted about recruitment?',
        options: [
          {
            id: '0',
            text: 'It has become a random population sample.',
          },
          {
            id: '1',
            text: 'Every nonvolunteer would respond identically.',
          },
          {
            id: '2',
            text: 'Selection into the study no longer matters.',
          },
          {
            id: '3',
            text: 'Random assignment alone does not make the volunteer sample representative.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'Sampling and treatment assignment address different inferential questions.',
        evidence: 'Sampling and treatment assignment address different inferential questions.',
        sectionIds: ['assignment'],
      },
      {
        id: 'b6-experiments-4',
        prompt:
          'A scorer does not know which lesson each essay’s author received. What bias is this intended to reduce?',
        options: [
          {
            id: '0',
            text: 'Assessment influenced by treatment expectations',
          },
          {
            id: '1',
            text: 'Seasonal recruitment differences',
          },
          {
            id: '2',
            text: 'All participant selection bias',
          },
          {
            id: '3',
            text: 'Every possible carryover effect',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Assessor blinding targets scoring influenced by knowledge of treatment.',
        evidence: 'Assessor blinding targets scoring influenced by knowledge of treatment.',
        sectionIds: ['control'],
      },
      {
        id: 'b6-experiments-5',
        prompt:
          'Every morning group gets A and every evening group gets B. What is the main design concern?',
        options: [
          {
            id: '0',
            text: 'The response was necessarily measured twice.',
          },
          {
            id: '1',
            text: 'Time of day is confounded with treatment.',
          },
          {
            id: '2',
            text: 'Random assignment guarantees no problem.',
          },
          {
            id: '3',
            text: 'The treatment has no definition.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'Time of day varies systematically with method and may affect outcomes.',
        evidence: 'Time of day varies systematically with method and may affect outcomes.',
        sectionIds: ['assignment'],
      },
      {
        id: 'b6-experiments-6',
        prompt: 'Which design correctly uses shelf position as a block in a plant experiment?',
        options: [
          {
            id: '0',
            text: 'Give one entire shelf A and another entire shelf B.',
          },
          {
            id: '1',
            text: 'Measure only the brightest shelf.',
          },
          {
            id: '2',
            text: 'Randomize A and B within each shelf-position block.',
          },
          {
            id: '3',
            text: 'Use shelf position as the response instead of growth.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'Each block should contain the treatment comparison.',
        evidence: 'Each block should contain the treatment comparison.',
        sectionIds: ['assignment', 'precision'],
      },
      {
        id: 'b6-experiments-7',
        prompt:
          'Ten measurements are taken from one treated tank. Why are they not ten independent treatment replicates?',
        options: [
          {
            id: '0',
            text: 'The treatment was assigned only once, to one tank.',
          },
          {
            id: '1',
            text: 'Repeated measurement becomes independent as soon as each reading uses a separate instrument.',
          },
          {
            id: '2',
            text: 'Each reading independently receives the treatment because its time of observation differs.',
          },
          {
            id: '3',
            text: 'Averaging the readings creates ten independently treated tanks for analysis.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Repeated readings do not create additional independently assigned units.',
        evidence: 'Repeated readings do not create additional independently assigned units.',
        sectionIds: ['comparison', 'precision'],
      },
      {
        id: 'b6-experiments-8',
        prompt: 'Why randomize the order of two tasks completed by each participant?',
        options: [
          {
            id: '0',
            text: 'To make every participant a random sample from the wider population',
          },
          {
            id: '1',
            text: 'To eliminate all carryover effects regardless of how long they last',
          },
          {
            id: '2',
            text: 'To turn the paired observations into measurements on independent participants',
          },
          {
            id: '3',
            text: 'To reduce systematic practice or fatigue effects tied to task order',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'Order randomization helps separate task effects from learning or fatigue.',
        evidence: 'Order randomization helps separate task effects from learning or fatigue.',
        sectionIds: ['precision'],
      },
      {
        id: 'b6-experiments-9',
        prompt:
          'Why might comparing only study completers undermine an initially randomized comparison?',
        options: [
          {
            id: '0',
            text: 'Randomization forbids any missing measurement.',
          },
          {
            id: '1',
            text: 'All missing data must have identical causes.',
          },
          {
            id: '2',
            text: 'Dropout may leave differently selected groups.',
          },
          {
            id: '3',
            text: 'Completers always have the same characteristics.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Treatment-related or outcome-related dropout can alter the compared populations.',
        evidence:
          'Treatment-related or outcome-related dropout can alter the compared populations.',
        sectionIds: ['assignment', 'precision'],
      },
      {
        id: 'b6-experiments-10',
        prompt: 'Which change most directly makes a vague outcome operational?',
        options: [
          {
            id: '0',
            text: 'Call the outcome “general improvement.”',
          },
          {
            id: '1',
            text: 'Specify dry plant mass measured after six weeks.',
          },
          {
            id: '2',
            text: 'Choose whichever outcome shows the largest difference afterward.',
          },
          {
            id: '3',
            text: 'Use different outcome definitions in each treatment group.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'A prespecified measurable outcome supports a clear comparison.',
        evidence: 'A prespecified measurable outcome supports a clear comparison.',
        sectionIds: ['comparison'],
      },
    ],
  },
];
